import { GoogleOAuthProvider } from "@react-oauth/google";
import useServerTime from "@repo/hooks/utils/useServerTime";
import type { ICustomerQuery } from "@repo/shopify/src/queries/customer-gql";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import PageTitle from "@components/atoms/PageTitle";

import EmailLogin from "@organisms/Login/EmailLogin";
import GoogleLogin from "@organisms/Login/GoogleLogin";
import KakaoLogin from "@organisms/Login/KakaoLogin";
import VerifyAccount from "@organisms/Login/VerifyAccount";

import useLayout from "@hooks/reducerHooks/useLayout";
import useMeta from "@hooks/reducerHooks/useMeta";
import useUser from "@hooks/reducerHooks/useUser";

import { URLS } from "@const/urls";

import { checkAccessAllowed } from "@utils/archive-sale";
import { NOTIFICATIONS } from "@utils/notifications";

const STEP = {
  LOGIN: "login",
  CERTIFICATION: "certification",
};

function Login() {
  const navigate = useNavigate();

  const { setMetaTag } = useMeta();
  const { customer, userLogout } = useUser();
  const { openToast, openModal, closeModal } = useLayout();
  const serverTime = useServerTime();

  /** 로그인 단계 */
  const [step, setStep] = useState(customer.phone && customer.birth?.value ? STEP.CERTIFICATION : STEP.LOGIN);

  useEffect(() => {
    setMetaTag({ title: `TUNE.KR - LOGIN` });
  }, []);

  /** 로그인 완료되었을 때 본인인증 여부 확인 */
  const onComplete = (accessToken?: string, customer?: ICustomerQuery["customer"]) => {
    if (!customer) return;

    const isAllowed = checkAccessAllowed({ customer, currentTime: serverTime });
    if (!isAllowed) {
      userLogout();
      navigate(URLS.HOME);

      openModal({
        title: "TUNE’s Archive Sale",
        description:
          "본 페이지는 TUNE 멤버십 Black / Platinum / Gold / Silver 회원을 대상으로 사전 진행되며, 이외 등급은 10월 30일 13시부터 접속 가능합니다.",
        confirmButton: {
          label: "Okay",
          onClick: () => {
            setStep(STEP.LOGIN);
            userLogout();
            navigate(URLS.HOME);

            closeModal();
          },
        },
      });

      return;
    }

    // 로그인일 때 userInfo phone 전화번호 있는지 확인.
    if (!customer.phone || !customer.birth?.value) {
      // phone 없을 경우 verify 단계로 변경
      openToast(NOTIFICATIONS.SUCCESS_LOGIN);
      setStep(STEP.CERTIFICATION);
      return;
    }

    // phone 있을 경우 로그인 모달 닫기
    navigate(URLS.HOME);
  };

  return (
    <section className="tune-column-4">
      {!customer.phone && (
        <section className="col-start-5 col-end-9">
          <PageTitle className="mb-10" title={step === STEP.LOGIN ? "WELCOME\nARCHIVE SALE" : "VERIFY\nACCOUNT"} />
          {step === STEP.LOGIN && (
            <>
              <div className="mb-5">
                <p>
                  TUNE 멤버십을 위한 Archive Sale 페이지 입니다.
                  <br />
                  회원가입 및 로그인 후, 프로모션 페이지로 이동합니다.
                </p>
              </div>
              {/* 소셜 로그인 */}
              <div className="flex flex-col gap-2.5">
                <KakaoLogin onComplete={onComplete} />
                <GoogleOAuthProvider clientId={import.meta.env.VITE_REACT_APP_GOOGLE_OAUTH_CLIENT_ID as string}>
                  <GoogleLogin onComplete={onComplete} />
                </GoogleOAuthProvider>
              </div>
              <div className="text-tuneGrey-400 pc:my-10 my-5 text-center text-sm">OR</div>
              {/* 이메일 로그인 */}
              <EmailLogin onComplete={onComplete} />
            </>
          )}
          {step === STEP.CERTIFICATION && <VerifyAccount redirectUrl={URLS.ACCOUNT_ORDER_HISTORY} />}
        </section>
      )}
    </section>
  );
}

export default Login;
